import { useState, useEffect } from 'react';
import { getAdminSuppliers, importCsv, importXlsx, getImportHistory } from '@/lib/api';
import { Upload, FileText, CheckCircle, AlertCircle, Eye, Table, Image, X, Info, FileSpreadsheet } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminCsvImport() {
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [delimiter, setDelimiter] = useState('auto');
  const [updateMode, setUpdateMode] = useState('smart');
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState('');
  const [uploading, setUploading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);

  useEffect(() => {
    getAdminSuppliers().then(r => { setSuppliers(r.data); if (r.data.length > 0) setSelectedSupplier(r.data[0].id); });
    getImportHistory().then(r => setHistory(r.data)).catch(() => {});
  }, []);

  const handlePreview = async () => {
    if (!file || fileType === 'xlsx') return; // Preview only for CSV
    setPreviewing(true);
    setPreview(null);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('delimiter', delimiter);
      const token = localStorage.getItem('amazonia_token');
      const res = await axios.post(`${BACKEND_URL}/api/admin/csv-preview`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
      });
      setPreview(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur de prévisualisation');
    }
    setPreviewing(false);
  };

  const handleImport = async () => {
    if (!file || !selectedSupplier) return;
    setError('');
    setResult(null);
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('supplier_id', selectedSupplier);
      formData.append('update_mode', updateMode);

      let res;
      if (fileType === 'xlsx') {
        res = await importXlsx(formData);
      } else {
        formData.append('delimiter', delimiter);
        res = await importCsv(formData);
      }
      setResult(res.data);
      setFile(null);
      setFileType('');
      setPreview(null);
      getImportHistory().then(r => setHistory(r.data)).catch(() => {});
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'import");
    }
    setUploading(false);
  };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    setFile(f);
    setPreview(null);
    setResult(null);
    setError('');
    if (f) {
      const ext = f.name.split('.').pop().toLowerCase();
      setFileType(ext === 'xlsx' || ext === 'xls' ? 'xlsx' : 'csv');
    }
  };

  const ACCEPTED_COLUMNS = [
    { group: 'Obligatoire', cols: [
      { name: 'SKU / Code / REF', desc: 'Identifiant unique du produit' },
    ]},
    { group: 'Produit', cols: [
      { name: 'Nom_Produit / Name / Nom', desc: 'Nom du produit' },
      { name: 'Description / Détail', desc: 'Description longue' },
      { name: 'Categorie / Category / Rayon', desc: 'Catégorie du produit' },
      { name: 'Sous_Categorie', desc: 'Sous-catégorie' },
      { name: 'Brand / Marque', desc: 'Marque du produit' },
    ]},
    { group: 'Prix & Stock', cols: [
      { name: 'Prix_Fournisseur_EUR / Price', desc: 'Prix fournisseur (HT)' },
      { name: 'Prix_Public_EUR', desc: 'Prix public (optionnel, sinon calculé via marge)' },
      { name: 'Conditionnement / Unit', desc: 'Ex: Caja x12, 500g' },
      { name: 'Poids / Weight', desc: 'Poids du produit' },
    ]},
    { group: 'Média & Livraison', cols: [
      { name: 'Image / Photo / Image_Fichier', desc: 'URL de l\'image OU image intégrée (XLSX)' },
      { name: 'Delai_Livraison_Semaines', desc: 'Délai de livraison en semaines' },
    ]},
  ];

  return (
    <div data-testid="admin-csv-import" className="p-6 lg:p-8">
      <h1 className="text-2xl font-extrabold text-[#111] tracking-tight mb-2">Import produits</h1>
      <p className="text-sm text-black/50 mb-8">Importez ou mettez à jour votre catalogue depuis un fichier <strong>XLSX (Excel)</strong> ou CSV</p>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Config */}
          <div className="border border-black/10 p-6">
            <h2 className="text-sm font-bold uppercase tracking-wider text-black/50 mb-4">1. Configuration</h2>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-black/50 font-semibold block mb-1.5">Fournisseur</label>
                <select value={selectedSupplier} onChange={(e) => setSelectedSupplier(e.target.value)} data-testid="csv-supplier-select" className="w-full px-3 py-2.5 text-sm border border-black/15 bg-white focus:ring-2 focus:ring-[#00A5FF] outline-none">
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              {fileType !== 'xlsx' && (
                <div>
                  <label className="text-xs uppercase tracking-wider text-black/50 font-semibold block mb-1.5">Séparateur CSV</label>
                  <select value={delimiter} onChange={(e) => setDelimiter(e.target.value)} className="w-full px-3 py-2.5 text-sm border border-black/15 bg-white focus:ring-2 focus:ring-[#00A5FF] outline-none">
                    <option value="auto">Auto-détection (recommandé)</option>
                    <option value=";">Point-virgule (;)</option>
                    <option value=",">Virgule (,)</option>
                    <option value="\t">Tabulation</option>
                  </select>
                </div>
              )}
              {fileType === 'xlsx' && (
                <div className="flex items-end">
                  <div className="flex items-center gap-2 px-3 py-2.5 bg-green-50 border border-green-200 rounded-sm">
                    <FileSpreadsheet size={16} className="text-green-600" />
                    <span className="text-sm font-medium text-green-700">Fichier Excel détecté — images intégrées supportées</span>
                  </div>
                </div>
              )}
            </div>

            {/* Update mode */}
            <div className="mb-4">
              <label className="text-xs uppercase tracking-wider text-black/50 font-semibold block mb-1.5">Mode de mise à jour (produits existants par SKU)</label>
              <div className="grid sm:grid-cols-3 gap-2">
                {[
                  { value: 'smart', label: 'Intelligent', desc: 'Met à jour les champs remplis, conserve les données existantes (photos, descriptions) si vide' },
                  { value: 'overwrite_all', label: 'Ecraser tout', desc: 'Ecrase toutes les données existantes avec le contenu du fichier' },
                  { value: 'prices_only', label: 'Prix uniquement', desc: 'Ne met à jour que les prix, conserve tout le reste (photos, noms, catégories)' },
                ].map((mode) => (
                  <button key={mode.value} type="button" onClick={() => setUpdateMode(mode.value)}
                    className={`p-3 text-left border transition-colors duration-200 ${updateMode === mode.value ? 'border-[#00A5FF] bg-[#00A5FF]/5' : 'border-black/10 hover:border-black/20'}`}>
                    <p className="text-xs font-bold text-[#111]">{mode.label}</p>
                    <p className="text-xs text-black/40 mt-1 leading-relaxed">{mode.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* File upload */}
            <div>
              <label className="text-xs uppercase tracking-wider text-black/50 font-semibold block mb-1.5">Fichier</label>
              <div className="border-2 border-dashed border-black/15 p-8 text-center hover:border-[#00A5FF] transition-colors duration-200 cursor-pointer" onClick={() => document.getElementById('import-file').click()}>
                {fileType === 'xlsx' ? <FileSpreadsheet size={28} className="mx-auto text-green-500 mb-3" /> : <Upload size={28} className="mx-auto text-black/20 mb-3" />}
                {file ? (
                  <div>
                    <p className="text-sm font-semibold text-[#111]">{file.name}</p>
                    <p className="text-xs text-black/40 mt-1">{(file.size / 1024).toFixed(1)} Ko — {fileType === 'xlsx' ? 'Excel (images intégrées supportées)' : 'CSV'}</p>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-black/50">Cliquez pour sélectionner un fichier <strong>XLSX</strong> ou CSV</p>
                    <p className="text-xs text-black/30 mt-1">Le format XLSX (Excel) permet d'inclure les images produits directement dans le tableau</p>
                  </div>
                )}
              </div>
              <input id="import-file" type="file" accept=".csv,.txt,.tsv,.xlsx,.xls" onChange={handleFileChange} className="hidden" data-testid="csv-file-input" />
            </div>

            {file && (
              <div className="flex gap-3 mt-4">
                {fileType !== 'xlsx' && (
                  <button onClick={handlePreview} disabled={previewing} data-testid="csv-preview-button"
                    className="flex items-center gap-2 px-4 py-2.5 border border-[#00A5FF] text-[#00A5FF] font-semibold text-sm rounded-sm hover:bg-[#00A5FF]/5 disabled:opacity-50 transition-colors duration-200">
                    <Eye size={14} /> {previewing ? 'Chargement...' : 'Prévisualiser'}
                  </button>
                )}
                <button onClick={handleImport} disabled={uploading} data-testid="csv-import-button"
                  className="flex items-center gap-2 px-6 py-2.5 bg-[#00A5FF] text-white font-semibold text-sm rounded-sm hover:bg-[#0090e0] disabled:opacity-50 transition-colors duration-200">
                  <Upload size={14} /> {uploading ? 'Import en cours...' : `Importer ${fileType === 'xlsx' ? 'le fichier Excel' : 'le CSV'}`}
                </button>
              </div>
            )}
          </div>

          {/* Preview (CSV only) */}
          {preview && (
            <div className="border border-[#00A5FF]/30 bg-[#00A5FF]/5">
              <div className="p-4 border-b border-[#00A5FF]/20 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Table size={16} className="text-[#00A5FF]" />
                  <h3 className="text-sm font-bold text-[#111]">Prévisualisation</h3>
                  <span className="text-xs text-black/40">~{preview.total_lines} lignes</span>
                </div>
                <button onClick={() => setPreview(null)} className="text-black/30 hover:text-black"><X size={16} /></button>
              </div>
              <div className="p-4">
                <p className="text-xs text-black/50 mb-2"><strong>Colonnes :</strong> {preview.columns.join(', ')}</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border border-black/10">
                    <thead><tr className="bg-[#F8F9FA]">{preview.columns.map((col, i) => <th key={i} className="text-left px-2 py-1.5 border-b border-r border-black/10 font-semibold whitespace-nowrap">{col}</th>)}</tr></thead>
                    <tbody>{preview.preview_rows.map((row, i) => <tr key={i} className="border-b border-black/5">{preview.columns.map((col, j) => <td key={j} className="px-2 py-1.5 border-r border-black/5 max-w-[200px] truncate">{row[col]?.startsWith?.('http') ? <span className="flex items-center gap-1 text-[#00A5FF]"><Image size={10} /> URL</span> : row[col] || '-'}</td>)}</tr>)}</tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Result */}
          {error && <div className="p-4 bg-red-50 border border-red-200"><p className="text-sm text-[#E8231B] flex items-center gap-1"><AlertCircle size={14} /> {error}</p></div>}
          {result && (
            <div className="border border-green-300 bg-green-50 p-6">
              <div className="flex items-center gap-2 text-green-700 mb-3"><CheckCircle size={18} /> <strong className="text-lg">Import terminé !</strong></div>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center p-3 bg-white border border-green-200">
                  <p className="text-2xl font-extrabold text-green-600">{result.added}</p>
                  <p className="text-xs text-green-600 font-medium">Ajoutés</p>
                </div>
                <div className="text-center p-3 bg-white border border-green-200">
                  <p className="text-2xl font-extrabold text-[#00A5FF]">{result.updated}</p>
                  <p className="text-xs text-[#00A5FF] font-medium">Mis à jour</p>
                </div>
                <div className="text-center p-3 bg-white border border-green-200">
                  <p className="text-2xl font-extrabold text-black/40">{result.skipped}</p>
                  <p className="text-xs text-black/40 font-medium">Ignorés</p>
                </div>
              </div>
              {result.images_found > 0 && (
                <p className="text-xs text-green-600 mb-2 flex items-center gap-1"><Image size={12} /> {result.images_found} images intégrées extraites du fichier Excel</p>
              )}
              {result.detected_columns?.length > 0 && (
                <p className="text-xs text-black/50 mb-2">Colonnes : {result.detected_columns.join(', ')}</p>
              )}
              {result.errors?.length > 0 && (
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200">
                  <p className="text-xs font-semibold text-yellow-700 mb-1">Avertissements :</p>
                  {result.errors.map((e, i) => <p key={i} className="text-xs text-yellow-600">{e}</p>)}
                </div>
              )}
            </div>
          )}

          {/* History */}
          <div className="border border-black/10">
            <div className="p-4 border-b border-black/10"><h2 className="text-sm font-bold uppercase tracking-wider text-black/50">Historique des imports</h2></div>
            {history.length === 0 ? (
              <div className="p-8 text-center text-sm text-black/40">Aucun import</div>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="bg-[#F8F9FA] text-xs uppercase tracking-wider text-black/50">
                  <th className="text-left px-4 py-2 font-semibold">Date</th>
                  <th className="text-left px-4 py-2 font-semibold">Fournisseur</th>
                  <th className="text-left px-4 py-2 font-semibold">Fichier</th>
                  <th className="text-center px-4 py-2 font-semibold">Ajoutés</th>
                  <th className="text-center px-4 py-2 font-semibold">MAJ</th>
                  <th className="text-center px-4 py-2 font-semibold">Ignorés</th>
                </tr></thead>
                <tbody>{history.map((h) => (
                  <tr key={h.id} className="border-t border-black/5">
                    <td className="px-4 py-2 text-xs text-black/50">{new Date(h.created_at).toLocaleString('fr-BE')}</td>
                    <td className="px-4 py-2 font-medium">{h.supplier_name}</td>
                    <td className="px-4 py-2 text-xs flex items-center gap-1">
                      {h.filename?.endsWith('.xlsx') ? <FileSpreadsheet size={12} className="text-green-500" /> : <FileText size={12} />} {h.filename}
                    </td>
                    <td className="px-4 py-2 text-center text-green-600 font-medium">{h.added}</td>
                    <td className="px-4 py-2 text-center text-[#00A5FF] font-medium">{h.updated}</td>
                    <td className="px-4 py-2 text-center text-black/40">{h.skipped}</td>
                  </tr>
                ))}</tbody>
              </table>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* XLSX Tip */}
          <div className="border-2 border-green-300 bg-green-50 p-5">
            <div className="flex items-center gap-2 mb-3">
              <FileSpreadsheet size={18} className="text-green-600" />
              <h3 className="text-sm font-bold text-green-800">Format XLSX recommandé</h3>
            </div>
            <p className="text-xs text-green-700 leading-relaxed mb-3">
              Utilisez un fichier <strong>Excel (.xlsx)</strong> pour importer vos produits avec leurs images directement intégrées dans les cellules du tableau.
            </p>
            <p className="text-xs text-green-600">Les images intégrées dans Excel seront automatiquement extraites et associées aux produits.</p>
          </div>

          {/* Column reference */}
          <div className="border border-black/10 p-5">
            <div className="flex items-center gap-2 mb-4">
              <Info size={16} className="text-[#00A5FF]" />
              <h3 className="text-sm font-bold text-[#111]">Colonnes acceptées</h3>
            </div>
            <p className="text-xs text-black/40 mb-4">Le système reconnaît automatiquement les noms de colonnes en FR, EN et ES.</p>
            {ACCEPTED_COLUMNS.map((group) => (
              <div key={group.group} className="mb-4">
                <p className="text-xs uppercase tracking-wider text-black/40 font-semibold mb-2">{group.group}</p>
                <div className="space-y-1.5">
                  {group.cols.map((col) => (
                    <div key={col.name} className="flex items-start gap-2">
                      <div className="w-1 h-1 bg-[#00A5FF] rounded-full mt-1.5 shrink-0" />
                      <div>
                        <p className="text-xs font-medium text-[#111]">{col.name}</p>
                        <p className="text-xs text-black/40">{col.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
